import Axios from "../utils/Axios";
import { isAxiosError } from "axios";

export type FabricPoint = [number, number];
export interface FabricComponent {
  id: string;
  name: string;
  width_mm: number;
  height_mm: number;
  contours: FabricPoint[][];
}
export interface FabricImport {
  filename: string;
  status: "verified" | "error";
  error?: string;
  warnings: string[];
  verification?: string;
  components: FabricComponent[];
}
export interface FabricNestRequest {
  fabric_height_mm: number;
  spacing_mm: number;
  edge_margin_mm: number;
  rotation_mode: "none" | "global_90";
  items: { verification: string; component_id: string; quantity: number }[];
}
export interface FabricLayout {
  rotation: 0 | 90;
  length_mm: number;
  utilization_percent: number;
  placements: {
    item_index: number;
    x_mm: number;
    y_mm: number;
    rotation: 0 | 90;
  }[];
}
export interface FabricNestResult {
  layouts: FabricLayout[];
  infeasible_orientations: { rotation: 0 | 90; reason: string }[];
  time_limited: boolean;
}

const positive = (n: number) => Number.isFinite(n) && n > 0;
const malformed = () =>
  new Error(
    "The fabric service returned incomplete or invalid data. Please retry or contact your administrator."
  );

export function validateFabricImports(items: FabricImport[], files: File[]) {
  if (!Array.isArray(items) || items.length !== files.length) throw malformed();
  const names = new Set<string>();
  for (const item of items) {
    if (
      !item ||
      !files.some((f) => f.name === item.filename) ||
      names.has(item.filename)
    )
      throw malformed();
    names.add(item.filename);
    if (
      !Array.isArray(item.warnings) ||
      item.warnings.some((w) => typeof w !== "string")
    )
      throw malformed();
    if (item.status === "error" && typeof item.error === "string" && item.error)
      continue;
    if (
      item.status !== "verified" ||
      !item.verification ||
      !Array.isArray(item.components) ||
      !item.components.length
    )
      throw malformed();
    const ids = new Set<string>();
    for (const c of item.components) {
      if (
        !c ||
        typeof c.id !== "string" ||
        !c.id ||
        ids.has(c.id) ||
        typeof c.name !== "string" ||
        !positive(c.width_mm) ||
        !positive(c.height_mm) ||
        !Array.isArray(c.contours) ||
        !c.contours.length
      )
        throw malformed();
      ids.add(c.id);
      for (const ring of c.contours) {
        if (
          !Array.isArray(ring) ||
          ring.length < 4 ||
          ring.some(
            (p) =>
              !Array.isArray(p) ||
              p.length !== 2 ||
              !p.every(Number.isFinite) ||
              p[0] < -0.001 ||
              p[1] < -0.001 ||
              p[0] > c.width_mm + 0.001 ||
              p[1] > c.height_mm + 0.001
          )
        )
          throw malformed();
        if (
          Math.hypot(
            ring[0][0] - ring[ring.length - 1][0],
            ring[0][1] - ring[ring.length - 1][1]
          ) > 0.001
        )
          throw malformed();
      }
    }
  }
  return items;
}

export function validateFabricResult(
  result: FabricNestResult,
  request: FabricNestRequest,
  components: FabricComponent[]
) {
  if (
    !result ||
    !Array.isArray(result.layouts) ||
    !Array.isArray(result.infeasible_orientations) ||
    typeof result.time_limited !== "boolean"
  )
    throw malformed();
  const expected = request.rotation_mode === "none" ? [0] : [0, 90];
  const seen = new Set<number>();
  for (const layout of result.layouts) {
    if (
      !layout ||
      !expected.includes(layout.rotation) ||
      seen.has(layout.rotation) ||
      !positive(layout.length_mm) ||
      !Number.isFinite(layout.utilization_percent) ||
      layout.utilization_percent <= 0 ||
      layout.utilization_percent > 100 ||
      !Array.isArray(layout.placements)
    )
      throw malformed();
    seen.add(layout.rotation);
    const counts = request.items.map(() => 0);
    for (const p of layout.placements) {
      if (
        !p ||
        p.rotation !== layout.rotation ||
        !Number.isInteger(p.item_index) ||
        !request.items[p.item_index] ||
        !Number.isFinite(p.x_mm) ||
        !Number.isFinite(p.y_mm)
      )
        throw malformed();
      const c = components[p.item_index];
      const w = p.rotation === 90 ? c.height_mm : c.width_mm;
      const h = p.rotation === 90 ? c.width_mm : c.height_mm;
      const margin = request.edge_margin_mm;
      if (
        p.x_mm < margin - 0.001 ||
        p.y_mm < margin - 0.001 ||
        p.x_mm + w > layout.length_mm - margin + 0.001 ||
        p.y_mm + h > request.fabric_height_mm - margin + 0.001
      )
        throw malformed();
      counts[p.item_index]++;
    }
    if (counts.some((count, i) => count !== request.items[i].quantity))
      throw malformed();
  }
  for (const entry of result.infeasible_orientations) {
    if (
      !entry ||
      !expected.includes(entry.rotation) ||
      seen.has(entry.rotation) ||
      typeof entry.reason !== "string" ||
      !entry.reason
    )
      throw malformed();
    seen.add(entry.rotation);
  }
  if (seen.size !== expected.length) throw malformed();
  return result;
}

export async function verifyFabricDxfs(
  files: File[],
  units: string
): Promise<FabricImport[]> {
  const body = new FormData();
  files.forEach((f) => body.append("files", f));
  body.append("unitless_units", units);
  const { data } = await Axios.post<{ items: FabricImport[] }>(
    "/costing/fabric/verify",
    body,
    { timeout: 120000 }
  );
  return validateFabricImports(data?.items, files);
}

export async function calculateFabricNesting(
  request: FabricNestRequest,
  components: FabricComponent[]
) {
  const { data } = await Axios.post<FabricNestResult>(
    "/costing/fabric/nest",
    request,
    { timeout: 120000 }
  );
  return validateFabricResult(data, request, components);
}

export function fabricErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    if (error.response?.status === 404)
      return "Fabric nesting is not available on this server yet. The fabric import and nesting backend endpoints must be installed.";
    if (typeof error.response?.data?.error === "string")
      return error.response.data.error;
    if (error.response?.status === 401)
      return "Your session has expired. Sign in and retry.";
    if (error.response?.status === 403)
      return "Your account does not have permission to use fabric nesting.";
    return "The fabric service could not complete this request. Check your connection and retry, or try fewer pieces.";
  }
  return error instanceof Error
    ? error.message
    : "Could not complete fabric nesting.";
}
